// src/hooks/useNotes.ts
import { useState, useEffect } from 'react';
import { EncryptionService } from '../utils/encryption';

const encryption = new EncryptionService();

export interface Note {
  id: string;
  title: string;
  content: string;
  category: string;
  date: string;
  type: 'password' | 'memo' | 'todo';
  tags: string[];
  links: string[];
  images: string[];
  username?: string;
  password?: string;
  due_date?: string;
  status?: '待處理' | '進行中' | '已完成' | '暫擱置';
  isArchived: boolean;
  isPinned: boolean;
  createdAt: number;
  updatedAt: number;
  noteKey?: string;
}

export interface EncryptedNote {
  id: string;
  title: string;
  content: string;
  category: string;
  date: string;
  type: string;
  tags: string;
  links: string;
  images: string;
  username: string;
  password: string;
  due_date: string;
  status: string;
  isArchived: number;
  isPinned: number;
  createdAt: number;
  updatedAt: number;
  iv: string;
  salt: string;
  note_key: string;
  note_key_iv: string;
  note_key_salt: string;
}

export type TodoStatus = '待處理' | '進行中' | '已完成' | '暫擱置';

export function useNotes(masterPassword: string, dek: CryptoKey | null, userId: string) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteKeyCache, setNoteKeyCache] = useState<Map<string, CryptoKey>>(new Map());

  const encryptWithDEK = async (data: string): Promise<string> => {
    if (!dek) throw new Error('DEK 未初始化');
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      dek,
      encoder.encode(data)
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  };

  const decryptWithDEK = async (base64Data: string): Promise<string> => {
    if (!dek) throw new Error('DEK 未初始化');
    const combined = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      dek,
      encrypted
    );
    return new TextDecoder().decode(decrypted);
  };

  const getNoteKey = async (noteId: string, encryptedNoteKey: string): Promise<CryptoKey> => {
    if (noteKeyCache.has(noteId)) {
      return noteKeyCache.get(noteId)!;
    }
    const noteKeyBuffer = await encryption.decryptNoteKeyWithDEK(encryptedNoteKey, dek!);
    const noteKey = await encryption.importNoteKey(noteKeyBuffer);
    setNoteKeyCache(prev => new Map(prev).set(noteId, noteKey));
    return noteKey;
  };

  const clearNoteKeyCache = (noteId?: string) => {
    if (noteId) {
      setNoteKeyCache(prev => {
        const newMap = new Map(prev);
        newMap.delete(noteId);
        return newMap;
      });
    } else {
      setNoteKeyCache(new Map());
    }
  };

  const getEncryptedNoteKey = async (noteId: string): Promise<string | null> => {
    const note = notes.find(n => n.id === noteId);
    if (!note || !note.noteKey) return null;
    return note.noteKey;
  };

  const loadNotes = async () => {
    if (!userId || !dek) {
      setNotes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/notes?userId=${userId}`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('請先登入');
        }
        throw new Error('載入失敗');
      }

      const encryptedNotes: EncryptedNote[] = await response.json();

      if (encryptedNotes.length === 0) {
        setNotes([]);
        return;
      }

      const decryptedNotes = await Promise.all(
        encryptedNotes.map(async (encNote) => {
          try {
            const noteKey = await getNoteKey(encNote.id, encNote.note_key);
            const content = await encryption.decryptWithNoteKey(encNote.content, noteKey);
            const title = await encryption.decryptWithNoteKey(encNote.title, noteKey);
            const tags = await encryption.decryptWithNoteKey(encNote.tags, noteKey);

            let images: string[] = [];
            if (encNote.images && encNote.images.length > 0) {
              try {
                const decryptedImages = await encryption.decryptWithNoteKey(encNote.images, noteKey);
                images = decryptedImages ? JSON.parse(decryptedImages) : [];
              } catch (imgErr) {
                images = [];
              }
            }

            let username = '';
            let password = '';
            let due_date = '';
            let status: TodoStatus | undefined = undefined;

            if (encNote.username && encNote.username.length > 0) {
              try { username = await encryption.decryptWithNoteKey(encNote.username, noteKey); } catch (e) { username = ''; }
            }
            if (encNote.password && encNote.password.length > 0) {
              try { password = await encryption.decryptWithNoteKey(encNote.password, noteKey); } catch (e) { password = ''; }
            }
            if (encNote.due_date && encNote.due_date.length > 0) {
              try { due_date = await encryption.decryptWithNoteKey(encNote.due_date, noteKey); } catch (e) { due_date = ''; }
            }
            if (encNote.status && encNote.status.length > 0) {
              try { status = await encryption.decryptWithNoteKey(encNote.status, noteKey) as TodoStatus; } catch (e) { status = undefined; }
            }

            return {
              ...encNote,
              title,
              content,
              tags: JSON.parse(tags),
              images,
              username,
              password,
              due_date,
              status,
              links: encNote.links ? JSON.parse(encNote.links) : [],
              isArchived: encNote.isArchived === 1,
              isPinned: encNote.isPinned === 1,
              noteKey: encNote.note_key,
            } as Note;
          } catch (err) {
            console.error('解密筆記失敗:', encNote.id, err);
            return null;
          }
        })
      );

      setNotes(decryptedNotes.filter((n): n is Note => n !== null));
    } catch (err) {
      console.error('載入筆記失敗:', err);
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const saveNote = async (note: Note) => {
    try {
      if (!dek) throw new Error('DEK 未初始化');
      if (!userId) throw new Error('請先登入');

      let noteKey: CryptoKey;
      let encryptedNoteKey: string;

      if (note.noteKey) {
        const noteKeyBuffer = await encryption.decryptNoteKeyWithDEK(note.noteKey, dek);
        noteKey = await encryption.importNoteKey(noteKeyBuffer);
        encryptedNoteKey = note.noteKey;
      } else {
        const noteKeyBuffer = encryption.generateNoteKey();
        noteKey = await encryption.importNoteKey(noteKeyBuffer.buffer);
        encryptedNoteKey = await encryption.encryptNoteKeyWithDEK(noteKeyBuffer.buffer, dek);
      }

      const encryptedContent = await encryption.encryptWithNoteKey(note.content || '', noteKey);
      const encryptedTitle = await encryption.encryptWithNoteKey(note.title || '無標題', noteKey);
      const encryptedTags = await encryption.encryptWithNoteKey(JSON.stringify(note.tags || []), noteKey);
      const encryptedImages = await encryption.encryptWithNoteKey(JSON.stringify(note.images || []), noteKey);
      const encryptedUsername = await encryption.encryptWithNoteKey(note.username || '', noteKey);
      const encryptedPassword = await encryption.encryptWithNoteKey(note.password || '', noteKey);
      const encryptedDueDate = await encryption.encryptWithNoteKey(note.due_date || '', noteKey);
      const encryptedStatus = await encryption.encryptWithNoteKey(note.status || '', noteKey);

      const encryptedNote: EncryptedNote = {
        id: note.id,
        title: encryptedTitle,
        content: encryptedContent,
        category: note.category || '一般',
        date: note.date || new Date().toISOString().split('T')[0],
        type: note.type || 'memo',
        tags: encryptedTags,
        links: JSON.stringify(note.links || []),
        images: encryptedImages,
        username: encryptedUsername,
        password: encryptedPassword,
        due_date: encryptedDueDate,
        status: encryptedStatus,
        isArchived: note.isArchived ? 1 : 0,
        isPinned: note.isPinned ? 1 : 0,
        createdAt: note.createdAt || Date.now(),
        updatedAt: Date.now(),
        iv: '',
        salt: '',
        note_key: encryptedNoteKey,
        note_key_iv: '',
        note_key_salt: '',
      };

      const response = await fetch(`/api/notes?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encryptedNote)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || '儲存失敗');
      }

      return encryptedNote;
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗');
      throw err;
    }
  };

	const createNote = async (noteData: Partial<Note>) => {
		try {
			const newNote: Note = {
				id: crypto.randomUUID(),
				title: noteData.title || '無標題',
				content: noteData.content || '',
				category: noteData.category || '一般',
				date: new Date().toISOString().split('T')[0],
				type: noteData.type || 'memo',
				tags: noteData.tags || [],
				links: noteData.links || [],
				images: noteData.images || [],
				username: noteData.username || '',
				password: noteData.password || '',
				due_date: noteData.due_date || '',
				status: noteData.status || (noteData.type === 'todo' ? '待處理' : undefined),
				isArchived: false,
				isPinned: false,
				createdAt: Date.now(),
				updatedAt: Date.now(),
			};

			// ✅ saveNote 會回傳 encryptedNote
			const encryptedNote = await saveNote(newNote);
			
			// ✅ 從 encryptedNote 取得 note_key
			newNote.noteKey = encryptedNote.note_key;
			
			// ✅ 更新狀態
			setNotes([newNote, ...notes]);
			return newNote;
		} catch (err) {
			setError(err instanceof Error ? err.message : '新增失敗');
			throw err;
		}
	};

  const updateNote = async (id: string, updates: Partial<Note>) => {
    try {
      const index = notes.findIndex(n => n.id === id);
      if (index === -1) throw new Error('筆記不存在');

      const updatedNote = {
        ...notes[index],
        ...updates,
        updatedAt: Date.now(),
        noteKey: notes[index].noteKey,
      };

      await saveNote(updatedNote);

      const updatedNotes = [...notes];
      updatedNotes[index] = updatedNote;
      setNotes(updatedNotes);
      return updatedNote;
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新失敗');
      throw err;
    }
  };

  const deleteNote = async (id: string) => {
    try {
      if (!userId) throw new Error('請先登入');

      const response = await fetch(`/api/notes?id=${id}&userId=${userId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('刪除失敗');
      }

      setNotes(notes.filter(n => n.id !== id));
      clearNoteKeyCache(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : '刪除失敗');
      throw err;
    }
  };

  const togglePin = async (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      await updateNote(id, { isPinned: !note.isPinned });
    }
  };

  const toggleArchive = async (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      await updateNote(id, { isArchived: !note.isArchived });
    }
  };

  const getNotesByType = (type?: Note['type']) => {
    if (!type) return notes;
    return notes.filter(n => n.type === type);
  };

  useEffect(() => {
    if (userId && dek) {
      loadNotes();
    } else {
      setNotes([]);
      setLoading(false);
    }
  }, [userId, dek]);

  return {
    notes,
    loading,
    error,
    createNote,
    updateNote,
    deleteNote,
    togglePin,
    toggleArchive,
    loadNotes,
    getNotesByType,
    getNoteKey,
    getEncryptedNoteKey,
    clearNoteKeyCache,
  };
}